import React, { useEffect, useState } from 'react';
import { useT } from '../../context/TenantContext';
import BackDeskLayout from '../../components/layout/BackDeskLayout';
import { api } from '../../services/api';

const PRIO_L = { critical:'Crítica', high:'Alta', medium:'Media', low:'Baja' };
const PRIO_C = { critical:'#B91C1C', high:'#0085CA', medium:'#B45309', low:'#15803D' };

function Stars({ rating, size = 16 }) {
  return (
    <span style={{ fontSize: size, letterSpacing: 1 }}>
      {[1,2,3,4,5].map(n => (
        <span key={n} style={{ color: n <= rating ? '#F59E0B' : '#D1D5DB' }}>★</span>
      ))}
    </span>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:8,
      padding:'20px 24px', minWidth:140 }}>
      <div style={{ fontSize:11, fontWeight:600, color:'#9CA3AF', textTransform:'uppercase', letterSpacing:.4, marginBottom:6 }}>{label}</div>
      <div style={{ fontSize:28, fontWeight:700, color:'#111827', lineHeight:1 }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:'#6B7280', marginTop:4 }}>{sub}</div>}
    </div>
  );
}

export default function SurveyResults() {
  const { t } = useT();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    api.getSurveyResults()
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const dist = data?.distribution || {};
  const maxDist = Math.max(...Object.values(dist), 1);

  return (
    <BackDeskLayout title={t('surveys.title')}>
      {loading && <div style={{ padding:40, textAlign:'center', color:'#9CA3AF' }}>Cargando…</div>}
      {error   && <div style={{ padding:40, textAlign:'center', color:'#B91C1C' }}>{error}</div>}

      {data && (
        <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

          {/* Estadísticas */}
          <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
            <StatCard
              label="Total respuestas"
              value={data.total}
            />
            <StatCard
              label="Valoración media"
              value={data.avg ? `${data.avg} ★` : '—'}
              sub={data.avg ? ['Muy mala','Mala','Regular','Buena','Excelente'][Math.round(data.avg)-1] : null}
            />
            {/* Distribución */}
            <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:8,
              padding:'20px 24px', flex:1, minWidth:220 }}>
              <div style={{ fontSize:11, fontWeight:600, color:'#9CA3AF', textTransform:'uppercase', letterSpacing:.4, marginBottom:12 }}>Distribución</div>
              {[5,4,3,2,1].map(n => (
                <div key={n} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:5 }}>
                  <span style={{ fontSize:12, width:70, flexShrink:0 }}>
                    {[1,2,3,4,5].map(i => (
                      <span key={i} style={{ color: i <= n ? '#F59E0B' : '#E5E7EB' }}>★</span>
                    ))}
                  </span>
                  <div style={{ flex:1, height:8, background:'#F3F4F6', borderRadius:4, overflow:'hidden' }}>
                    <div style={{ width:`${((dist[n]||0)/maxDist)*100}%`, height:'100%',
                      background:'#F59E0B', borderRadius:4, transition:'width .3s' }}/>
                  </div>
                  <span style={{ fontSize:11, color:'#6B7280', width:20, textAlign:'right' }}>{dist[n]||0}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Tabla de respuestas */}
          {data.total === 0 ? (
            <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:8,
              padding:48, textAlign:'center', color:'#9CA3AF' }}>
              Aún no hay respuestas de encuesta
            </div>
          ) : (
            <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:8, overflow:'hidden' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr style={{ background:'#F9FAFB', borderBottom:'1px solid #E5E7EB' }}>
                    <th style={{ padding:'10px 16px', textAlign:'left', fontWeight:600, color:'#6B7280', fontSize:11, textTransform:'uppercase', letterSpacing:.4 }}>Ticket</th>
                    <th style={{ padding:'10px 16px', textAlign:'left', fontWeight:600, color:'#6B7280', fontSize:11, textTransform:'uppercase', letterSpacing:.4 }}>Solicitante</th>
                    <th style={{ padding:'10px 16px', textAlign:'left', fontWeight:600, color:'#6B7280', fontSize:11, textTransform:'uppercase', letterSpacing:.4 }}>Agente</th>
                    <th style={{ padding:'10px 16px', textAlign:'left', fontWeight:600, color:'#6B7280', fontSize:11, textTransform:'uppercase', letterSpacing:.4 }}>Prioridad</th>
                    <th style={{ padding:'10px 16px', textAlign:'left', fontWeight:600, color:'#6B7280', fontSize:11, textTransform:'uppercase', letterSpacing:.4 }}>{t('surveys.rating')}</th>
                    <th style={{ padding:'10px 16px', textAlign:'left', fontWeight:600, color:'#6B7280', fontSize:11, textTransform:'uppercase', letterSpacing:.4 }}>{t('surveys.comment')}</th>
                    <th style={{ padding:'10px 16px', textAlign:'left', fontWeight:600, color:'#6B7280', fontSize:11, textTransform:'uppercase', letterSpacing:.4 }}>Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((row, i) => (
                    <tr key={row.id}
                      style={{ borderBottom: i < data.data.length-1 ? '1px solid #F3F4F6' : 'none' }}>
                      <td style={{ padding:'12px 16px' }}>
                        <div style={{ fontWeight:600, color:'#111827', maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row.title}</div>
                        <div style={{ fontSize:11, color:'#9CA3AF', fontFamily:'monospace', marginTop:2 }}>{row.ref}</div>
                      </td>
                      <td style={{ padding:'12px 16px', color:'#374151' }}>{row.requester}</td>
                      <td style={{ padding:'12px 16px', color:'#374151' }}>{row.assignee_name || <span style={{color:'#9CA3AF'}}>—</span>}</td>
                      <td style={{ padding:'12px 16px' }}>
                        <span style={{ fontSize:11, fontWeight:600, color: PRIO_C[row.priority] }}>
                          {PRIO_L[row.priority] || row.priority}
                        </span>
                      </td>
                      <td style={{ padding:'12px 16px' }}>
                        <Stars rating={row.survey_rating} />
                      </td>
                      <td style={{ padding:'12px 16px', color:'#374151', maxWidth:240 }}>
                        {row.survey_comment
                          ? <span style={{ fontStyle:'italic' }}>"{row.survey_comment}"</span>
                          : <span style={{ color:'#9CA3AF' }}>—</span>}
                      </td>
                      <td style={{ padding:'12px 16px', color:'#9CA3AF', whiteSpace:'nowrap', fontSize:12 }}>
                        {row.survey_at ? new Date(row.survey_at).toLocaleDateString('es-ES', { day:'2-digit', month:'short', year:'numeric' }) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </BackDeskLayout>
  );
}
